import React, { useEffect, useMemo, useState } from 'react';
import { Chain, ChainStep, Pipeline } from '../../types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import VisualChainBuilder from '../../components/VisualChainBuilder';
import { apiGet, apiPost } from '../../utils/apiClient';

export default function ChainDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chain, setChain] = useState<Chain | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet(`/api/chains/${id}`);
      if (!res.ok) throw new Error(`Failed to load chain (${res.status})`);
      const data = await res.json();
      setChain(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    const loadPipes = async () => {
      try {
        const res = await apiGet('/api/pipelines');
        if (res.ok) {
          const data = await res.json();
          setPipelines(Array.isArray(data) ? data : []);
        }
      } catch {}
    };
    loadPipes();
  }, []);

  const execute = async () => {
    if (!id) return;
    try {
      const res = await apiPost(`/api/chains/${id}/execute`);
      if (!res.ok) throw new Error(`Failed to start chain (${res.status})`);
      const data = await res.json();
      alert(`Chain run started: ${data.run_id}`);
    } catch (e: any) {
      alert(e?.message || 'Failed to execute');
    }
  };

  const steps = useMemo(() => chain?.steps || [], [chain]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0 }}>{chain?.name || 'Chain'}</h2>
          {chain && <p style={{ marginTop: 6, color: '#6b7280' }}>{chain.description || 'No description'}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={execute} style={{ border: '1px solid #d1fae5', background: '#ecfdf5', color: '#065f46', padding: '10px 14px', borderRadius: 8 }}>Execute</button>
          {id && <Link to={`/chains/${id}/edit`} style={{ border: '1px solid #e5e7eb', background: 'white', padding: '10px 14px', borderRadius: 8, textDecoration: 'none', color: '#374151' }}>Edit</Link>}
          <Link to="/chains" style={{ border: '1px solid #e5e7eb', background: 'white', padding: '10px 14px', borderRadius: 8, textDecoration: 'none', color: '#374151' }}>Back</Link>
        </div>
      </div>

      {loading && <div style={{ marginTop: 16, color: '#6b7280' }}>Loading...</div>}
      {error && <div style={{ marginTop: 16, color: '#b91c1c' }}>{error}</div>}

      {chain && (
        <div style={{ marginTop: 16, background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 4px 6px rgba(0,0,0,0.08)' }}>
          <h3 style={{ marginTop: 0, color: '#1f2937' }}>Visual</h3>
          <VisualChainBuilder pipelines={pipelines} value={steps as ChainStep[]} onChange={() => {}} readOnly={true} />

          <h3 style={{ marginTop: 16, color: '#1f2937' }}>Steps</h3>
          <ol style={{ marginTop: 8 }}>
            {steps.map((s, idx) => (
              <li key={idx} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700 }}>{s.pipeline_id}</div>
                <div style={{ color: '#6b7280', fontSize: 12 }}>depends_on: {(s.depends_on && s.depends_on.length > 0) ? s.depends_on.join(', ') : 'none'}</div>
              </li>
            ))}
            {steps.length === 0 && <li style={{ color: '#6b7280' }}>No steps defined.</li>}
          </ol>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Raw JSON</div>
            <pre style={{ background: '#f9fafb', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>{JSON.stringify(chain, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
